import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, Trash2 } from "lucide-react";

export interface StaffingLineItem {
  id: string;
  roleType: string;
  quantity: number;
  armedStatus: "armed" | "unarmed";
  clearanceLevel: string;
  payRate: number;
  hoursPerDay: number;
  daysPerWeek: number;
}

// Roles that are always available regardless of service/property type
const ALWAYS_AVAILABLE_ROLES = [
  "Site Manager",
  "Supervisor",
  "Drone Operator",
  "Drone Pilot (FAA Part 107)",
  "Other",
] as const;

// Roles allowed per service type. Always-available roles are appended automatically.
const ROLES_BY_SERVICE: Record<string, string[]> = {
  standing_guard: ["Officer", "Roaming Guard", "Stationed Guard", "Concierge / Lobby", "Access Control Officer"],
  mobile_patrol: ["Patrol Driver", "Roaming Guard", "Officer", "Armed Response Officer"],
  fire_watch: ["Fire Watch", "Officer"],
  event_security: ["Roaming Guard", "Stationed Guard", "Event Staff", "Officer", "Crowd Control Officer", "Access Control Officer"],
  alarm_response: ["Armed Response Officer", "Patrol Driver", "Officer"],
  executive_protection: ["Executive Protection Agent", "Officer"],
  loss_prevention: ["Loss Prevention", "Officer", "Stationed Guard"],
  concierge_security: ["Concierge / Lobby", "Officer", "Stationed Guard"],
  construction_site: ["Officer", "Roaming Guard", "Stationed Guard", "Patrol Driver"],
  bank_security: ["Bank Lobby Officer", "Bank Operations Monitor", "Officer", "Stationed Guard"],
  bank_armed_guard: ["Bank Armed Guard", "Bank Vault Guard", "Bank Lobby Officer", "Drive-Through / ATM Officer"],
  atm_courier: ["Cash-in-Transit Officer", "Drive-Through / ATM Officer", "Bank Armed Guard"],
  government_contract: ["Facility Security Officer (FSO)", "Cleared Guard (Secret)", "Cleared Guard (Top Secret)", "Access Control Officer", "Roving Patrol (Government)", "Physical Security Specialist"],
  cleared_facility: ["Facility Security Officer (FSO)", "Cleared Guard (Secret)", "Cleared Guard (Top Secret)", "SCIF Security Specialist", "Access Control Officer", "Physical Security Specialist"],
  access_control_scif: ["SCIF Security Specialist", "Cleared Guard (Top Secret)", "Access Control Officer", "Facility Security Officer (FSO)"],
  military_base: ["Cleared Guard (Secret)", "Cleared Guard (Top Secret)", "Access Control Officer", "Roving Patrol (Government)", "Armed Response Officer"],
  other: [],
};

// Fallback by property type when service type is missing/unknown
const ROLES_BY_PROPERTY: Record<string, string[]> = {
  event_venue: ["Roaming Guard", "Stationed Guard", "Event Staff", "Crowd Control Officer", "Access Control Officer", "Officer"],
  bank_branch: ["Bank Lobby Officer", "Bank Armed Guard", "Bank Operations Monitor"],
  credit_union: ["Bank Lobby Officer", "Bank Armed Guard", "Bank Operations Monitor"],
  financial_institution: ["Bank Lobby Officer", "Bank Armed Guard", "Bank Operations Monitor"],
  bank_corporate_office: ["Bank Lobby Officer", "Bank Operations Monitor", "Concierge / Lobby"],
  bank_data_center: ["Access Control Officer", "Stationed Guard", "Bank Operations Monitor"],
  construction_site: ["Officer", "Roaming Guard", "Stationed Guard", "Patrol Driver"],
  government_facility: ["Facility Security Officer (FSO)", "Cleared Guard (Secret)", "Access Control Officer", "Physical Security Specialist"],
  military_installation: ["Cleared Guard (Secret)", "Cleared Guard (Top Secret)", "Roving Patrol (Government)", "Armed Response Officer"],
  embassy_consulate: ["Cleared Guard (Top Secret)", "Access Control Officer", "Armed Response Officer"],
  federal_building: ["Facility Security Officer (FSO)", "Access Control Officer", "Cleared Guard (Secret)"],
  data_center_scif: ["SCIF Security Specialist", "Cleared Guard (Top Secret)", "Access Control Officer"],
  defense_contractor: ["Facility Security Officer (FSO)", "Cleared Guard (Secret)", "Physical Security Specialist"],
};

// Default catalog used when nothing is selected yet
const ALL_ROLES = [
  "Officer", "Roaming Guard", "Stationed Guard", "Event Staff", "Crowd Control Officer",
  "Patrol Driver", "Executive Protection Agent", "Fire Watch", "Loss Prevention",
  "Concierge / Lobby", "Bank Lobby Officer", "Bank Armed Guard", "Bank Vault Guard",
  "Drive-Through / ATM Officer", "Cash-in-Transit Officer", "Bank Operations Monitor",
  "Facility Security Officer (FSO)", "Cleared Guard (Secret)", "Cleared Guard (Top Secret)",
  "SCIF Security Specialist", "Access Control Officer", "Roving Patrol (Government)",
  "Armed Response Officer", "Physical Security Specialist",
];

export function getAvailableRoles(serviceType?: string, propertyType?: string): string[] {
  const fromService = serviceType ? ROLES_BY_SERVICE[serviceType] : undefined;
  const fromProperty = propertyType ? ROLES_BY_PROPERTY[propertyType] : undefined;
  let base: string[];
  if (fromService && fromService.length > 0) {
    base = [...fromService, ...(fromProperty || [])];
  } else if (fromProperty && fromProperty.length > 0) {
    base = [...fromProperty];
  } else {
    base = [...ALL_ROLES];
  }
  // Merge with always-available, dedupe, preserve order
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const r of [...base, ...ALWAYS_AVAILABLE_ROLES]) {
    if (!seen.has(r)) {
      seen.add(r);
      merged.push(r);
    }
  }
  return merged;
}

const CLEARANCE_LEVELS = [
  { value: "none", label: "None" },
  { value: "public_trust", label: "Public Trust" },
  { value: "secret", label: "Secret" },
  { value: "top_secret", label: "Top Secret" },
  { value: "ts_sci", label: "TS/SCI" },
] as const;

const HOURS_OPTIONS = Array.from({ length: 24 }, (_, i) => i + 1);
const DAYS_OPTIONS = Array.from({ length: 7 }, (_, i) => i + 1);

export function createStaffingLineItem(): StaffingLineItem {
  return {
    id: crypto.randomUUID(),
    roleType: "Officer",
    quantity: 1,
    armedStatus: "unarmed",
    clearanceLevel: "none",
    payRate: 25,
    hoursPerDay: 8,
    daysPerWeek: 5,
  };
}

export function calculateLineItemWeekly(item: StaffingLineItem): number {
  return item.quantity * item.payRate * item.hoursPerDay * item.daysPerWeek;
}

export function calculateStaffingTotals(items: StaffingLineItem[]) {
  const weekly = items.reduce((sum, item) => sum + calculateLineItemWeekly(item), 0);
  return { weekly, monthly: weekly * 4.33, yearly: weekly * 52 };
}

export function totalOfficerCount(items: StaffingLineItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

export function staffingFormula(items: StaffingLineItem[]): string {
  return items
    .map(
      (item) => {
        const clearance = item.clearanceLevel && item.clearanceLevel !== "none" ? ` [${item.clearanceLevel.replace(/_/g, " ").toUpperCase()}]` : "";
        return `${item.quantity} ${item.armedStatus} ${item.roleType}(s)${clearance} × $${item.payRate}/hr × ${item.hoursPerDay}hrs × ${item.daysPerWeek}days`;
      }
    )
    .join(" + ");
}

export const GOVERNMENT_PROPERTY_TYPES = [
  "government_facility", "military_installation", "embassy_consulate",
  "federal_building", "data_center_scif", "defense_contractor",
] as const;

export const GOVERNMENT_SERVICE_TYPES = [
  "government_contract", "cleared_facility", "access_control_scif", "military_base",
] as const;

export function requiresClearance(propertyType?: string, serviceType?: string): boolean {
  return GOVERNMENT_PROPERTY_TYPES.includes(propertyType as any) ||
    GOVERNMENT_SERVICE_TYPES.includes(serviceType as any);
}

interface StaffingTableProps {
  items: StaffingLineItem[];
  onChange: (items: StaffingLineItem[]) => void;
  showClearance?: boolean;
  serviceType?: string;
  propertyType?: string;
}

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });

// Event-style engagements bill per event (single or multi-day), not weekly/monthly/yearly
const EVENT_SERVICE_TYPES = new Set(["event_security"]);
const EVENT_PROPERTY_TYPES = new Set(["event_venue"]);
function isEventEngagement(serviceType?: string, propertyType?: string) {
  return (
    (serviceType ? EVENT_SERVICE_TYPES.has(serviceType) : false) ||
    (propertyType ? EVENT_PROPERTY_TYPES.has(propertyType) : false)
  );
}

const StaffingTable = ({ items, onChange, showClearance = false, serviceType, propertyType }: StaffingTableProps) => {
  const availableRoles = getAvailableRoles(serviceType, propertyType);
  const isEvent = isEventEngagement(serviceType, propertyType);
  const daysLabel = isEvent ? "Event Days" : "Days/Week";
  // Ensure any currently-selected roles remain visible even if not in the filtered list
  const rolesForItem = (current: string) =>
    availableRoles.includes(current) ? availableRoles : [current, ...availableRoles];

  const updateItem = (id: string, updates: Partial<StaffingLineItem>) => {
    onChange(items.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  const removeItem = (id: string) => {
    if (items.length <= 1) return;
    onChange(items.filter((item) => item.id !== id));
  };

  const addItem = () => {
    onChange([...items, createStaffingLineItem()]);
  };

  const totals = calculateStaffingTotals(items);

  return (
    <Card className="border-border bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Users className="h-5 w-5 text-primary" />
          Multi-Role Staffing
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Add multiple positions with individual rates and schedules.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="rounded-lg border border-border bg-secondary/50 p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">
                Position {index + 1}
              </span>
              {items.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => removeItem(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Role Type */}
              <div>
                <label className="text-xs text-muted-foreground">Role</label>
                <Select
                  value={item.roleType}
                  onValueChange={(v) => updateItem(item.id, { roleType: v })}
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-foreground h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {rolesForItem(item.roleType).map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Armed Status */}
              <div>
                <label className="text-xs text-muted-foreground">Armed Status</label>
                <Select
                  value={item.armedStatus}
                  onValueChange={(v) =>
                    updateItem(item.id, { armedStatus: v as "armed" | "unarmed" })
                  }
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-foreground h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="unarmed">Unarmed</SelectItem>
                    <SelectItem value="armed">Armed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Security Clearance - only for government contracts */}
              {showClearance && (
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground">Security Clearance</label>
                  <Select
                    value={item.clearanceLevel}
                    onValueChange={(v) => updateItem(item.id, { clearanceLevel: v })}
                  >
                    <SelectTrigger className="mt-1 bg-secondary border-border text-foreground h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {CLEARANCE_LEVELS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="text-xs text-muted-foreground">Quantity</label>
                <Input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) =>
                    updateItem(item.id, { quantity: Math.max(1, Number(e.target.value)) })
                  }
                  className="mt-1 bg-secondary border-border text-foreground h-9 text-sm"
                />
              </div>

              {/* Pay Rate */}
              <div>
                <label className="text-xs text-muted-foreground">Pay Rate ($/hr)</label>
                <div className="relative mt-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                  <Input
                    type="number"
                    min={0}
                    value={item.payRate || ""}
                    onChange={(e) =>
                      updateItem(item.id, { payRate: Number(e.target.value) })
                    }
                    className="pl-6 bg-secondary border-border text-foreground h-9 text-sm"
                  />
                </div>
              </div>

              {/* Hours per Day */}
              <div>
                <label className="text-xs text-muted-foreground">Hours/Day</label>
                <Select
                  value={String(item.hoursPerDay)}
                  onValueChange={(v) =>
                    updateItem(item.id, { hoursPerDay: Number(v) })
                  }
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-foreground h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {HOURS_OPTIONS.map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Days per Week / Event Days */}
              <div>
                <label className="text-xs text-muted-foreground">{daysLabel}</label>
                <Select
                  value={String(item.daysPerWeek)}
                  onValueChange={(v) =>
                    updateItem(item.id, { daysPerWeek: Number(v) })
                  }
                >
                  <SelectTrigger className="mt-1 bg-secondary border-border text-foreground h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {(isEvent ? Array.from({ length: 30 }, (_, i) => i + 1) : DAYS_OPTIONS).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line item total */}
            <div className="flex justify-between items-center pt-1 border-t border-border/50">
              <span className="text-xs text-muted-foreground">
                {item.quantity} × ${item.payRate}/hr × {item.hoursPerDay}hrs × {item.daysPerWeek}{isEvent ? (item.daysPerWeek === 1 ? "day" : "days") : "days"}
              </span>
              <span className="text-sm font-bold text-primary">
                {fmt(calculateLineItemWeekly(item))}{isEvent ? (item.daysPerWeek === 1 ? "/day" : "/event") : "/wk"}
              </span>
            </div>
          </div>
        ))}

        <Button
          variant="outline"
          onClick={addItem}
          className="w-full border-dashed border-border text-muted-foreground hover:text-foreground"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Position
        </Button>

        {/* Totals */}
        <div className="rounded-lg border border-border bg-secondary p-4 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Total Personnel</span>
            <span className="font-semibold text-foreground">
              {totalOfficerCount(items)}
            </span>
          </div>
          {isEvent ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Event Total</span>
                <span className="text-lg font-bold text-primary">{fmt(totals.weekly)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Per Day Avg</p>
                  <p className="font-bold text-primary">
                    {fmt(
                      totals.weekly /
                        Math.max(1, Math.max(...items.map((i) => i.daysPerWeek)))
                    )}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Per Officer</p>
                  <p className="font-bold text-primary">
                    {fmt(totals.weekly / Math.max(1, totalOfficerCount(items)))}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Weekly</span>
                <span className="text-lg font-bold text-primary">{fmt(totals.weekly)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Monthly</p>
                  <p className="font-bold text-primary">{fmt(totals.monthly)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Yearly</p>
                  <p className="font-bold text-primary">{fmt(totals.yearly)}</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Breakdown */}
        <div className="rounded-lg gradient-accent border border-primary/20 p-3">
          <p className="text-xs text-muted-foreground font-medium mb-1">Staffing Breakdown</p>
          {items.map((item) => {
            const clearance = item.clearanceLevel && item.clearanceLevel !== "none" ? ` [${item.clearanceLevel.replace(/_/g, " ").toUpperCase()}]` : "";
            const dayUnit = isEvent ? (item.daysPerWeek === 1 ? "day" : "event days") : "days/wk";
            const totalUnit = isEvent ? (item.daysPerWeek === 1 ? "/day" : "/event") : "/wk";
            return (
              <p key={item.id} className="text-xs text-muted-foreground">
                {item.quantity} {item.armedStatus} {item.roleType}(s){clearance} — ${item.payRate}/hr — {item.hoursPerDay}hrs/day — {item.daysPerWeek} {dayUnit} = {fmt(calculateLineItemWeekly(item))}{totalUnit}
              </p>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default StaffingTable;
